import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import * as React from 'react';
import { useRef, useState } from 'react';
import OWButton from '../../../components/ow_button/OWButton';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_CONTENT_INSET, OSCAR_CONTENT_SCROLL_INSET } from '../../constants/AIAgentConstants';
import { AGENT_TEAM_REQUEST_TOOL, AgentTranscriptEntry } from '../../../types/agentHarness';
import { OSCAR_ICON_SIZE } from '../../constants/AIAgentConstants';
import OscarIconButton from '../shared/OscarIconButton';
import { humanizeToolName } from '../../utils/OscarLabels';
import OscarApprovalCard from './OscarApprovalCard';
import OscarTeamRequestCard from './OscarTeamRequestCard';

type ApprovalEntry = Extract<AgentTranscriptEntry, { kind: 'approval' }>;

interface OscarApprovalQueueProps {
	/** One run of consecutive approvals, in the order the agent queued them. */
	approvals: ApprovalEntry[];
	onResolve?: (approvalId: string, approved: boolean) => void | Promise<boolean>;
	className?: string;
}

/** Scroll step: one card plus the gap between cards. */
const CARD_STEP = 328;

/**
 * A batch of pending approvals as a horizontal queue.
 *
 * Asking about thirteen work orders can queue thirteen writes, and stacked
 * vertically they bury the conversation and make the reader scroll past
 * near-identical cards to reach the composer. Side by side, the batch reads as
 * one decision with thirteen items.
 *
 * "Approve all" is offered only when the batch is genuinely one decision: every
 * call is the same tool and none is flagged destructive. A mixed batch, or
 * anything destructive, still takes an answer per card — bulk-approving a
 * delete is precisely what the write gate exists to prevent.
 */
export const OscarApprovalQueue = ({
	approvals,
	onResolve,
	className,
}: OscarApprovalQueueProps) => {
	const scrollerRef = useRef<HTMLDivElement>(null);
	// Approve all fired every request and left every control live, so the batch
	// could be approved and then rejected before the first `approval_resolved`
	// came back — and arrival order decided which writes ran. The per-card lock
	// cannot see a bulk decision, so the lock has to live up here.
	//
	// Per approval id, not one flag for the batch: a bulk decision can partly
	// succeed, and a single flag cleared on the first failure unlocked the ones
	// that had already landed — reopening the same conflicting-decision hole for
	// whichever approvals succeeded while their `approval_resolved` was in
	// flight. Only the id that actually failed may unlock.
	const [lockedIds, setLockedIds] = useState<{ [approvalId: string]: true }>({});
	// Drives the bulk buttons' own busy state, which is about the batch rather
	// than any one approval.
	const [bulkInFlight, setBulkInFlight] = useState(false);

	const unlock = (approvalId: string) =>
		setLockedIds((current) => {
			const next = { ...current };
			delete next[approvalId];
			return next;
		});

	const pending = approvals.filter((approval) => !approval.resolved);
	const toolNames = pending.map((approval) => approval.toolName);
	const sameTool = toolNames.every((name) => name === toolNames[0]);
	const anyDestructive = pending.some((approval) => approval.destructive);
	// A team request shares what the customer is asking with OpenWrench staff,
	// so each one is its own decision — never swept up in a bulk approve.
	const teamRequestsOnly = sameTool && toolNames[0] === AGENT_TEAM_REQUEST_TOOL;
	const canBulkResolve = pending.length > 1 && sameTool && !anyDestructive && !teamRequestsOnly;
	// What a bulk decision would actually act on. An approval already locked by
	// an earlier round is spoken for, so re-sending it is exactly the conflict
	// this guards against — and the button must not count it either.
	const bulkTargets = pending.filter((approval) => !lockedIds[approval.id]);

	/**
	 * One card's own decision, registered in the queue's lock.
	 *
	 * The card holds a `deciding` flag, but only the queue can see the batch: a
	 * card locked to itself was still counted as a bulk target, so approving one
	 * card and then hitting Reject all sent both decisions for it — the same
	 * conflict as the bulk case, entered from the other side.
	 */
	const resolveOne = (approvalId: string, approved: boolean): Promise<boolean> => {
		setLockedIds((current) => {
			const next = { ...current };
			next[approvalId] = true;
			return next;
		});
		return Promise.resolve(onResolve(approvalId, approved)).then(
			(landed) => {
				if (landed === false) {
					unlock(approvalId);
					return false;
				}
				return true;
			},
			(resolveError) => {
				unlock(approvalId);
				throw resolveError;
			}
		);
	};

	const scrollBy = (direction: number) => {
		if (scrollerRef.current) {
			scrollerRef.current.scrollBy({ left: direction * CARD_STEP, behavior: 'smooth' });
		}
	};

	const resolveAll = (approved: boolean) => {
		if (!onResolve || bulkInFlight || !bulkTargets.length) {
			return;
		}
		setBulkInFlight(true);
		setLockedIds((current) => {
			const next = { ...current };
			bulkTargets.forEach((approval) => {
				next[approval.id] = true;
			});
			return next;
		});
		// Each decision settles on its own. Unlocked only where it did not land:
		// on success the stream retires the card, and re-enabling would briefly
		// offer a choice already made.
		Promise.all(
			bulkTargets.map((approval) =>
				Promise.resolve(onResolve(approval.id, approved))
					.then((landed) => {
						if (landed === false) {
							unlock(approval.id);
						}
					})
					.catch(() => unlock(approval.id))
			)
			// Every branch above is handled, so this settles rather than rejects.
		).then(() => setBulkInFlight(false));
	};

	return (
		<div className={cn('flex w-full min-w-0 flex-col gap-xs', className)}>
			<div className={cn('flex w-full items-center gap-xs', OSCAR_CONTENT_INSET)}>
				<span className="bodymedium min-w-0 flex-1 truncate text-grey-10">
					{pending.length
						? `${pending.length} approval${pending.length === 1 ? '' : 's'} needed`
						: 'All approvals resolved'}
					{sameTool && toolNames.length && !teamRequestsOnly ? (
						<span className="footnoteregular text-character-secondary">{` · ${humanizeToolName(
							toolNames[0]
						)}`}</span>
					) : null}
				</span>
				{approvals.length > 1 ? (
					<div className="flex shrink-0 items-center gap-2xs">
						<OscarIconButton
							icon={<LeftOutlined />}
							label="Previous approval"
							size={OSCAR_ICON_SIZE.chevron}
							onClick={() => scrollBy(-1)}
						/>
						<OscarIconButton
							icon={<RightOutlined />}
							label="Next approval"
							size={OSCAR_ICON_SIZE.chevron}
							onClick={() => scrollBy(1)}
						/>
					</div>
				) : null}
			</div>

			{canBulkResolve ? (
				<div className={cn('flex w-full items-center gap-xs', OSCAR_CONTENT_INSET)}>
					<Button
						size="large"
						disabled={bulkInFlight || !bulkTargets.length}
						onClick={() => resolveAll(false)}
					>
						{`Reject all ${bulkTargets.length}`}
					</Button>
					<OWButton
						size="large"
						type="primary"
						loading={bulkInFlight}
						disabled={!bulkTargets.length}
						onClick={() => resolveAll(true)}
						className="!border-[#54298e] !bg-[#54298e]"
					>
						{`Approve all ${bulkTargets.length}`}
					</OWButton>
				</div>
			) : null}
			{pending.length > 1 && !canBulkResolve ? (
				<span className={cn('footnoteregular text-character-secondary', OSCAR_CONTENT_INSET)}>
					{anyDestructive
						? 'One of these is destructive, so each needs its own answer.'
						: teamRequestsOnly
						? 'Each request to the OpenWrench team needs its own answer.'
						: 'These call different tools, so each needs its own answer.'}
				</span>
			) : null}

			{/* Native horizontal scrolling, so trackpad and touch work; the buttons
			    are for mouse users. Snap keeps a card aligned after a flick. */}
			{/* Full bleed: the scroller spans the transcript edge to edge, and the
			    padding lives inside it so the first card still lines up with the
			    text above while the run can scroll clear of the gutter.
			    `scroll-px-m` makes snap points respect that inset. */}
			<div
				ref={scrollerRef}
				className={cn(
					'flex w-full snap-x snap-mandatory gap-xs overflow-x-auto pb-2xs',
					OSCAR_CONTENT_INSET,
					OSCAR_CONTENT_SCROLL_INSET
				)}
			>
				{approvals.map((approval) => (
					<div key={approval.id} className="w-80 shrink-0 snap-start">
						{/* No rationale here: the team request's own `request` and `details`
						    are the model's explanation, and the harness does not ask for one. */}
						{approval.toolName === AGENT_TEAM_REQUEST_TOOL ? (
							<OscarTeamRequestCard
								args={approval.args}
								resolved={approval.resolved}
								approved={approval.approved}
								onResolve={onResolve ? (approved) => resolveOne(approval.id, approved) : undefined}
								locked={!!lockedIds[approval.id]}
							/>
						) : (
							<OscarApprovalCard
								toolName={approval.toolName}
								args={approval.args}
								destructive={approval.destructive}
								rationale={approval.rationale}
								resolved={approval.resolved}
								approved={approval.approved}
								onResolve={onResolve ? (approved) => resolveOne(approval.id, approved) : undefined}
								locked={!!lockedIds[approval.id]}
							/>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

export default OscarApprovalQueue;
